import { endOfDay, subMonths } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isShallowEqual } from 'remeda'
import { useWeekStartsOn } from '~/hooks'

import {
  Button,
  Calendar,
  CalendarProps,
  DateRange,
  Icon,
  OnSelectHandler,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
  usePrevious,
} from '@hap/components'
import { Timespan } from '@hap/utils'

interface Props {
  mode?: CalendarProps['mode']
  value: Timespan.Info | null
  onValueChange: (timespan: Timespan.Info) => void
}

export function getVisibleMonthFromTimespan(
  value: Timespan.Info | null,
  fallbackMonth: Date,
  weekStartsOn: Timespan.DayOfWeek,
) {
  const staticTimespan = Timespan.toStaticTimespan(value, weekStartsOn)
  return staticTimespan.start ?? fallbackMonth
}

export function DateRangePicker(props: Props) {
  const weekStartsOn = useWeekStartsOn()
  const staticTimespan = Timespan.toStaticTimespan(props.value, weekStartsOn)
  const previousTimespan = usePrevious(props.value)
  const today = useMemo(() => new Date(), [])
  const oneMonthAgo = useMemo(() => subMonths(today, 1), [today])

  const [open, setOpen] = useState(false)
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>({
    from: staticTimespan.start,
    to: staticTimespan.end,
  })
  const [visibleMonth, setVisibleMonth] = useState<Date>(() =>
    getVisibleMonthFromTimespan(props.value, oneMonthAgo, weekStartsOn ?? 1),
  )

  useEffect(() => {
    if (!isShallowEqual(previousTimespan, props.value)) {
      const staticTimespan = Timespan.toStaticTimespan(props.value, weekStartsOn)
      setCustomDateRange({
        from: staticTimespan.start,
        to: staticTimespan.end,
      })
      setVisibleMonth(getVisibleMonthFromTimespan(props.value, oneMonthAgo, weekStartsOn))
    }
  }, [oneMonthAgo, previousTimespan, props.value, weekStartsOn])

  const handleSelect = useCallback<OnSelectHandler<DateRange>>(
    (nextRange, selectedDay) => {
      setCustomDateRange((range) => {
        if (!range) return
        if (range.from && range.to) return { from: selectedDay }
        return nextRange as DateRange
      })
    },
    [setCustomDateRange],
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={'outline'}
          className={cn(
            'justify-start text-left font-normal',
            !props.value && 'text-muted-foreground',
          )}
        >
          <Icon
            name="calendar"
            weight="duotone"
            className="mr-1 -ml-1.5 size-5 opacity-60"
          />
          {Timespan.humanize(staticTimespan) || <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="flex w-auto items-stretch p-0">
        <div className="flex shrink-0 flex-col gap-1 border-r px-2 py-4">
          {DATE_PRESETS.map((preset) => (
            <Button
              size="sm"
              onClick={() => {
                setOpen(false)
                props.onValueChange({ type: 'preset', value: preset.value })
              }}
              variant={
                props.value?.type === 'preset' && preset.value === props.value.value
                  ? 'ghost-active'
                  : 'ghost'
              }
              className="w-full justify-start text-left"
              key={preset.value}
            >
              {preset.label}
            </Button>
          ))}
        </div>
        <div>
          <Calendar
            numberOfMonths={2}
            mode="range"
            required={true}
            selected={customDateRange}
            onSelect={handleSelect}
            weekStartsOn={weekStartsOn}
            month={visibleMonth}
            onMonthChange={setVisibleMonth}
            initialFocus
            disabled={{ after: today }}
            showOutsideDays={false}
            captionLayout="dropdown"
          />
          <div className="flex items-center justify-end gap-4 border-t px-2 py-4">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setOpen(false)
                if (customDateRange?.from) {
                  props.onValueChange({
                    type: 'static',
                    start: customDateRange.from,
                    end: endOfDay(customDateRange.to || customDateRange.from),
                  })
                }
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export const DATE_PRESETS = [
  { label: 'Today', value: 'Today' },
  { label: 'Yesterday', value: 'Yesterday' },
  { label: 'This Week', value: 'ThisWeek' },
  { label: 'Last 7 Days', value: 'Last7Days' },
  { label: 'Last 14 Days', value: 'Last14Days' },
  { label: 'Last Week', value: 'LastWeek' },
  { label: 'This Month', value: 'ThisMonth' },
  { label: 'Last Month', value: 'LastMonth' },
  { label: 'Last 6 Months', value: 'Last6Months' },
  { label: 'Last 12 Months', value: 'Last12Months' },
] satisfies Array<{ label: string; value: Timespan.Preset }>
